import React from 'react'
import { array, string, object, oneOfType, bool } from 'prop-types'
import { register, format } from 'timeago.js'
import { SwiperSlide } from 'swiper/react'
import Case from 'case'
import { FormattedMessage } from 'react-intl'
import { urlWithTrack } from '../../helpers/common'
import { LanguageContextConsumer } from '../../providers/LanguageProvider'
import { sendClickEventToInsights } from '../../utils/insights'
import { EMPTY_ARRAY, NUMERAL } from '../../utils/constants'
import AdSwiper from '../ui/ad-swiper/AdSwiper'
import AdLink from '../ui/ad-link/AdLink'
import SaveButton from '../ui/button/SaveButton'
import Image from '../ui/image/Image'
import { projectMessages as messages } from './messages'
import Insights from './Insights'

register('es', require('timeago.js/lib/lang/es'))
register('pt', require('timeago.js/lib/lang/pt_BR'))
register('zh', require('timeago.js/lib/lang/zh_CN'))

const Project = ({
  architects = EMPTY_ARRAY,
  categories,
  companies,
  images = EMPTY_ARRAY,
  insights,
  location,
  publicationDate,
  title,
  url = NUMERAL,
  type,
  gridView
}) => {
  const handleLinkClick = () => {
    sendClickEventToInsights(insights.category, insights.label, insights.value, insights.version)
  }
  const trackedUrl = urlWithTrack(url, type)
  const saveButtonImage = images.length > 0 ? images[0].url_medium : ''

  return (
    <Insights {...insights}>
      <div className='object-container project-object'>
        {/* Image */}
        <div className='object-image project-object__image-container'>
          {images.length > 1 ? (
            <AdSwiper isLazy isVirtual>
              {images.map((image, index) => (
                <SwiperSlide lazy='true' key={index} virtualIndex={index}>
                  <div className='project-object__image-wrapper' >
                    <AdLink href={trackedUrl} onClick={handleLinkClick}>
                      {/* eslint-disable-next-line max-len */}
                      <Image src={image.url_medium} data-object-fit='cover' className='swiper-lazy' loading='lazy' alt={image.image_alt} />
                    </AdLink>
                  </div>
                </SwiperSlide>
              ))}
            </AdSwiper>
          ) : (
            <div className='project-object__image-wrapper'>
              <AdLink href={trackedUrl} onClick={handleLinkClick}>
                <Image src={images.length === 1 ? images[0].url_medium : ''} data-object-fit='cover' alt={images[0].image_alt} />
              </AdLink>
            </div>
          )}
          <SaveButton title={title} image={saveButtonImage} url={url} gridView={gridView} insights={insights} />
        </div>

        {/* title */}
        <h3 className='object-title project-object__title afd-title-big'>
          <AdLink className='afd-title--black-link' href={trackedUrl} onClick={handleLinkClick}>
            <span itemProp='name'>{title}</span>
          </AdLink>
        </h3>

        {/* metadata */}
        <ul className='object-metadata project-object__metadata'>
          {!!publicationDate && (
            <li>
              <LanguageContextConsumer>
                {context => Case.sentence(format(publicationDate, context.state.locale))}
              </LanguageContextConsumer>
            </li>
          )}
          <li className={publicationDate ? 'object-metadata__extra project-object-metadata__extra' : ''}>
            <FormattedMessage {...messages.project} />
          </li>
        </ul>

        {/* content */}
        <div className='object-content'>
          {/* architects */}
          {architects.length > 0 && (
            <p className='project-object__content'>
              <span className='object-content__bold'>
                <FormattedMessage {...messages.architects} />
                {': '}
              </span>
              {architects.map((item, index) => (
                <AdLink key={index} href={urlWithTrack(item.url, type)}>
                  {!!index && ', '}
                  {item.name}
                </AdLink>
              ))}
            </p>
          )}

          {/* location */}
          {!!location && (
            <p className='project-object__content'>
              <span className='object-content__bold'>
                <FormattedMessage {...messages.location} />
                {': '}
              </span>
              {location}
            </p>
          )}

          {/* categories */}
          {categories.length > 0 && (
            <p>
              <span className='object-content__bold'>
                <FormattedMessage {...messages.categories} />
                {': '}
              </span>
              {categories.map((item, index) => (
                <AdLink key={index} href={urlWithTrack(item.url, type)}>
                  {!!index && ', '}
                  {item.name}
                </AdLink>
              ))}
            </p>
          )}

          {/* companies */}
          {companies.length && (
            <p>
              <span className='object-content__bold'>
                <FormattedMessage {...messages.companies} />
                {': '}
              </span>
              {companies.slice(0, 3).map((item, index) => (
                <AdLink key={index} href={urlWithTrack(item.url, type)}>
                  {!!index && ', '}
                  {item.name}
                </AdLink>
              ))}
            </p>
          )}
        </div>
      </div>
    </Insights>
  )
}

Project.propTypes = {
  architects: array,
  categories: array,
  companies: oneOfType([string, array, object]),
  images: array,
  location: string,
  publicationDate: string,
  title: string,
  url: string,
  insights: object,
  type: string,
  gridView: bool
}

export default React.memo(Project)
